/**
 * useNavBadges — v.1-D §7.14.
 *
 * Aggregates the four count badges shown in the SideNav:
 *   - channelsLive       — channels currently in `status === 'live'`.
 *   - alertsCount        — critical alerts from `api.alerts.getCriticalAlertCount`.
 *   - pendingAutomations — `api.automations.list` filtered to `status === 'pending'`.
 *   - pendingIngestion   — length of `api.ingestJobs.getPendingIngestJobs`.
 *
 * Every field defaults to 0 while the underlying query is `undefined` so the
 * SideNav can render zero-state badges from first paint without flicker.
 */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import { useChannelList } from './useChannelList';

export interface NavBadges {
  channelsLive: number;
  alertsCount: number;
  pendingAutomations: number;
  pendingIngestion: number;
}

interface AutomationRow {
  status: string;
}

export function useNavBadges(): NavBadges {
  const channels = useChannelList();
  const alertCount = useQuery(api.alerts.getCriticalAlertCount) as
    | number
    | undefined;
  const automations = useQuery(api.automations.list) as
    | AutomationRow[]
    | undefined;
  const pending = useQuery(api.ingestJobs.getPendingIngestJobs) as
    | unknown[]
    | undefined;

  return {
    channelsLive:
      channels?.filter((c) => c.status === 'live').length ?? 0,
    alertsCount: alertCount ?? 0,
    pendingAutomations:
      automations?.filter((a) => a.status === 'pending').length ?? 0,
    pendingIngestion: pending?.length ?? 0,
  };
}
